import 'react-datepicker/dist/react-datepicker.css'

import React from 'react'
import { Controller } from 'react-hook-form'

import {
  DATEPICKER_DATE_FORMAT,
  DATE_FORMAT_CALENDAR,
} from '@/constants/global'

import { Label } from './components/Label'
import { getMode } from './helpers'
import { useDatePicker } from './hooks'

import { DatePicker } from '../DatePicker'
import { FormItem } from '../FormItem'

import type { DatepickerProps } from './types'

/**
 * DatePickerForm component based on {@link https://github.com/Hacker0x01/react-datepicker react-datepicker}
 * @component
 * @param {string} dateStartKey - which subkey will the start date will have in the react form object?
 * @param {string} dateEndKey - which subkey will the end date will have in the react form object?
 * @param {string} label - text label above Input
 * @param {string} name - form field key
 * @param {function} onChange - onChange handler
 * @param {boolean} selectsRange - allow to chose range of dates
 * @param {boolean} isClearable - allow to clear input
 * @param {string} dateFormat - override date format, default format is 'MMM dd, yyyy'
 */
export const DatePickerForm: React.FC<DatepickerProps> = ({
  isRequired,
  dateStartKey,
  dateEndKey,
  label,
  name,
  monthsShown = 2,
  onChange,
  readOnly,
  dataCy,
  selected,
  selectsRange,
  isWrapperSmall = false,
  children,
  className,
  error: propsError,
  errorMessage,
  isFullWidth = true,
  dateFormat,
  ...restProps
}) => {
  const { showYearPicker, showMonthYearPicker, rules } = restProps
  const mode = getMode({ showYearPicker, showMonthYearPicker })
  const {
    control,
    startDate,
    endDate,
    selectedDate,
    handleDatesChange,
    error,
  } = useDatePicker({
    isRequired,
    dateStartKey,
    dateEndKey,
    name,
    onChange,
    selected,
    mode,
    selectsRange,
  })

  return (
    <Controller
      control={control}
      name={name}
      rules={rules}
      render={({ field: { ref, ...restOfField } }) => (
        <FormItem
          label={label && <Label name={label} />}
          error={error || propsError || { message: errorMessage }}
          isFullWidth={isFullWidth}
        >
          <DatePicker
            ref={(datePickerRef) => {
              const { input } =
                (datePickerRef as unknown as {
                  input: HTMLInputElement
                }) || {}
              if (input) {
                ref(input)
              }
            }}
            {...restOfField}
            error={Boolean(error || propsError)}
            data-cy={dataCy}
            isWrapperSmall={isWrapperSmall}
            className={className}
            startDate={startDate}
            endDate={endDate}
            selected={selectedDate}
            dateFormatCalendar={DATE_FORMAT_CALENDAR}
            dateFormat={dateFormat || DATEPICKER_DATE_FORMAT}
            monthsShown={monthsShown}
            onChange={handleDatesChange}
            selectsRange={selectsRange}
            readOnly={readOnly}
            {...restProps}
          >
            {children}
          </DatePicker>
        </FormItem>
      )}
    />
  )
}
